import fromPairs from 'lodash/fromPairs';
import groupBy from 'lodash/groupBy';
import { ActionIconsForAccordion } from 'core/constants/common';
import { FieldEntitiesType, PreDefinedFieldEntityType } from 'core/constants/report';
import {
  DrilldownConfigMapping, MeasureIdentifier, MeasureIdentifiers, MeasureListPayload,
} from 'redux-v2/report-builder/report-builder-store.state';
import { IColumn, IField } from 'core/models/report-response';
import { displayName, isValidArrayAndNotEmpty, isValidObjectAndNotEmpty } from 'components/feature/Report/ReportSidebar/common/helpers';
import { IAccordianListItem, IAccordionList } from 'components/common/SideTray/side-tray.interface';
import { IBuilderMeasure } from 'core/models/report-builder/report-builder';
import useLocalize from 'components/common/Localization/useLocalize.hook';
import {
  FieldEntities, getFieldIdentifier, getTextTooltipContentForMeasure, isDrilldownPossible,
} from 'core/utils/report.util';
import { findUnderlyingColumn } from 'core/utils/report-expressions';
import isEmpty from 'lodash/isEmpty';
import { ISideTrayProps } from './drilldown.interface';

export const sideTrayProps: ISideTrayProps = {
  sideTrayPlacement: 'right',
  sideTrayPosition: 478,
};

// returns Icon for sidetray based on entity type.
// @entity: name of the entity.
export const getIconBasedOnEntityType = (entity: string): ActionIconsForAccordion => {
  switch (entity) {
    case FieldEntitiesType.Custom:
      return ActionIconsForAccordion.CUSTOM;
    case PreDefinedFieldEntityType:
      return ActionIconsForAccordion.PREDEFINED;
    default:
      return null;
  }
};

// returns the boolean if drilldown has any empty accordiaon
// @drilldown: DrilldownConfigMapping for measures.
export const isDrilldownEmptyAccord = (drilldown: DrilldownConfigMapping): boolean => {
  const keys = isValidObjectAndNotEmpty(drilldown) ? Object.keys(drilldown) : [];
  return keys?.some((key: string) => drilldown[key]?.length === 0);
};

// returns the entity name.
// @col: measure payload.
export const getEntity = (col: IColumn): FieldEntitiesType | typeof PreDefinedFieldEntityType => {
  if (col?.Props?.Measure?.IsPreDefined) {
    return PreDefinedFieldEntityType;
  } if (col?.BuilderConfig?.Entity === FieldEntitiesType.Custom) {
    return FieldEntitiesType.Custom;
  } if (col?.BuilderConfig?.EntityName) {
    return col?.BuilderConfig?.EntityName as FieldEntitiesType;
  }
  return (col?.Props?.Namespace || col?.BuilderConfig?.Entity) as FieldEntitiesType;
};

// remove the empty measures and return the updated measure list.
// @measure: measures list.
export const removeEmptyMeasures = (measure: MeasureListPayload) => Object.fromEntries(
  Object.entries(measure)?.filter(([key, value]) => value.length > 0),
);

// returns the measure info based on name
// @id: measures id.
// @appliedMeasures: all the measures which are applied.
export const getData = (id: string, appliedMeasures: IColumn[]): IColumn => appliedMeasures?.find((measure: IColumn) => measure?.Id === id);

// returns the boolean if it is cross entity
// @selectedMeasureEntity: current selected entity.
// @col: Column info
// @drilldownConfigMapping: mapping for measures
// @activeId: current key for active accorion
const getCrossEntity = (selectedMeasureEntity: Omit<FieldEntitiesType, FieldEntitiesType.Custom>, colEntity: Omit<FieldEntitiesType, FieldEntitiesType.Custom>, selectedMeasureJoinID: string,
  colMeasureJoinID: string): boolean => (selectedMeasureEntity !== colEntity || selectedMeasureJoinID !== colMeasureJoinID);

// returns the tooltip content based on the custom expression or cross entity.
// @isCrossEntity: boolean for cross entity.
// @isCalculatedColumn: boolean for custom expression.
// @measure: measure details.
const getTooltipContent = (isCrossEntity: boolean, isCalculatedColumn: boolean): string => {
  if (isCalculatedColumn) {
    return `${useLocalize('reportBuilder.calculatedValuesTooltip')}`;
  }
  if (isCrossEntity) {
    return `${useLocalize('reportBuilder.crossEntityValueAlert')}`;
  }
  return null;
};

// returns the grouping for sidetray with respective measure grouping
// @appliedMeasures: All applied measures from data tab.
// @activeId: Current active accoridan key.
// @calculatedColumns: Calculated column list.

export const getGroupedMeasures = (fieldsData: Array<IField>, appliedMeasures: IColumn[], drilldownConfigMapping: DrilldownConfigMapping, activeId: string, calculatedColumns: IBuilderMeasure[], defaultEntity: string, builderMeasuresList:IBuilderMeasure[]): IAccordionList[] => {
  const isCalculated = fromPairs((calculatedColumns || []).map((column) => [column?.Alias, true]));
  const appliedColumnsWithoutDrilldownConfig = appliedMeasures.filter((column: IColumn) => !column.Props.DrilldownEnabled);
  const selectedMeasure: IColumn = appliedMeasures?.find((measure: IColumn) => drilldownConfigMapping?.[activeId]?.[0]?.Id === measure.Id);
  let selectedMeasureEntity = selectedMeasure?.Props?.Measure?.Entity;
  let selectedMeasureJoinID = selectedMeasure?.Props?.Measure?.JoinID;

  if (selectedMeasure?.Props?.Measure?.IsCustomExpression || selectedMeasure?.Props?.IsSourceCustomColumn) {
    const { Entity, JoinId } = getEntityJoinIdForCustomMeasure(fieldsData, selectedMeasure, defaultEntity);
    selectedMeasureEntity = Entity;
    selectedMeasureJoinID = JoinId;
  }
  const groupedData = groupBy(appliedColumnsWithoutDrilldownConfig, (measures: IColumn) => getEntity(measures));
  const groupedDataKeys = Object.keys(groupedData);

  const groupedMeasures = groupedDataKeys.map((entityName: string) => {
    const filteredMeasuresBasedOnEntity = isValidArrayAndNotEmpty(appliedColumnsWithoutDrilldownConfig) ? appliedColumnsWithoutDrilldownConfig.filter((col: IColumn) => getEntity(col) === entityName) : [];

    const measureCol = filteredMeasuresBasedOnEntity?.find((measure: IColumn) => measure?.BuilderConfig?.NamespaceEntity || measure?.BuilderConfig?.Entity);
    const namespaceEntity = measureCol?.BuilderConfig?.NamespaceEntity || measureCol?.BuilderConfig?.Entity;
    const namespaceDisplayName = measureCol?.BuilderConfig?.NsDisplayName;
    const accordianValues = filteredMeasuresBasedOnEntity
      ?.map((measure: IColumn): IAccordianListItem => {
        let colMeasureEntity = measure.Props?.Measure?.Entity;
        let colMeasureJoinID = measure.Props?.Measure?.JoinID;
        if (measure.Props?.Measure?.IsCustomExpression || measure.Props?.IsSourceCustomColumn) {
          const { Entity, JoinId } = getEntityJoinIdForCustomMeasure(fieldsData, measure, defaultEntity);
          colMeasureEntity = Entity;
          colMeasureJoinID = JoinId;
        }
        const isCrossEntity = drilldownConfigMapping?.[activeId]?.length ? getCrossEntity(selectedMeasureEntity,
          colMeasureEntity, selectedMeasureJoinID, colMeasureJoinID) : false;

        let expr = measure?.Props?.Measure?.Expression;
        if (!expr) {
          const currentMeasure = builderMeasuresList.find((builderMeasure: IBuilderMeasure) => builderMeasure.Alias === measure.Name);
          expr = currentMeasure?.MeasureExpression;
        }
        const isCalculatedMeasure = isCalculated[measure.Name] || !isDrilldownPossible(expr);
        return {
          name: displayName(measure),
          uniqueKey: getFieldIdentifier(measure),
          toolTipContent: getTextTooltipContentForMeasure(measure),
          data: measure,
          renderIcon: ActionIconsForAccordion.ADD,
          renderDispayIcon: getIconBasedOnEntityType(entityName),
          isItemDisable: isCrossEntity || isCalculatedMeasure,
          iconItemToolTipText: getTooltipContent(isCrossEntity, isCalculatedMeasure),
        };
      });
    const isUnionTable = namespaceDisplayName === FieldEntitiesType.UnionTable;
    const getHeaderIcon = () => {
      if (isUnionTable) return FieldEntities[FieldEntitiesType.UnionTable];
      if (FieldEntities[namespaceEntity]) return FieldEntities[namespaceEntity];
      return FieldEntities.Query;
    };
    return {
      groupId: entityName,
      headerName: isEmpty(namespaceDisplayName) ? entityName : namespaceDisplayName,
      headerIcon: getHeaderIcon(),
      accordValues: accordianValues,
      isCustomExp: false,
      openAccordSectionOnInitialLoad: entityName === PreDefinedFieldEntityType, // to open accord section for pre-defined //dynamic entity based accodian should be open by default
    };
  });

  return groupedMeasures;
};

// returns the formatted column payload with entity and joinId to get default drilldown columns
// @nonExistColumns: list of measure which doesn't have any default columns.
// @appliedMeasures: Current applied measures.
// @drilldownConfigMapping: Mapping for current applied measures in drilldown tab.
// @fields: Field info to get joinId and entity info.
export const getMeasureIdentifiersPayload = (nonExistColumns: string[], appliedMeasures: IColumn[], drilldownConfigMapping: DrilldownConfigMapping, fields: IField[], defaultEntity: string) => nonExistColumns?.reduce((acc: MeasureIdentifiers, id): MeasureIdentifiers => {
  const measureInfo = appliedMeasures?.find((measure: IColumn) => measure?.Id === drilldownConfigMapping?.[id]?.[0]?.Id);
  if (measureInfo?.Props?.Measure?.IsPreDefined) {
    acc[id] = {
      Entity: measureInfo?.Props?.Measure?.Entity as string,
      JoinId: measureInfo?.Props?.Measure?.JoinID || '',
    };
  } else if (measureInfo?.Props?.Measure?.IsCustomExpression || measureInfo?.Props?.IsSourceCustomColumn) {
    const { Entity, JoinId } = getEntityJoinIdForCustomMeasure(fields, measureInfo, defaultEntity);
    acc[id] = {
      Entity,
      JoinId,
    };
  } else {
    acc[id] = {
      Entity: measureInfo?.Props?.Measure?.Entity as string,
      JoinId: measureInfo?.Props?.Measure?.JoinID || '',
    };
  }
  return acc;
}, {} as MeasureIdentifiers);

export const getEntityJoinIdForCustomMeasure = (fieldsData: Array<IField>, newMeasure: IColumn, fallbackEntity: string) => {
  const data: MeasureIdentifier = {
    Entity: '',
    JoinId: '',
  };
  let fieldInfo = null;
  if (!newMeasure?.Props?.IsSourceCustomColumn) {
    const column = findUnderlyingColumn(newMeasure?.Props?.Measure?.Expression);
    fieldInfo = !isValidArrayAndNotEmpty(fieldsData) ? {
      Entity: fallbackEntity || '',
      JoinID: '',
    } : fieldsData?.find((field) => {
      let res = field?.Alias === column?.Column?.SchemaName;
      if (column?.PrependTableName && column?.TableNameOverride) {
        res = field?.JoinID === column?.TableNameOverride && field?.Alias === column?.Column?.SchemaName;
      }
      if (column?.PrependTableName && !column?.TableNameOverride) {
        res = field?.JoinID === column?.TableNameOverride;
      }
      return res;
    });
  }
  data.Entity = fieldInfo?.Entity || fallbackEntity || '';
  data.JoinId = fieldInfo?.JoinID || '';
  return data;
};

// returns the entity and join ID for a measure
// @fieldsData: Array of field metadata containing entity and join information
// @newMeasure: The measure column to analyze
// @fallbackEntity: Default entity to use if no entity is found
// Returns: MeasureIdentifier containing Entity and JoinId strings
export const getEntityJoinIdForMeasure = (fieldsData: Array<IField>, newMeasure: IColumn, fallbackEntity: string): MeasureIdentifier => {
  // if the measure is not a custom expression, return entity and joinId from measure properties
  if (!newMeasure?.Props?.Measure?.IsCustomExpression) {
    const result: MeasureIdentifier = { Entity: '', JoinId: '' };
    result.Entity = newMeasure?.Props?.Measure?.Entity as string || fallbackEntity || '';
    result.JoinId = newMeasure?.Props?.Measure?.JoinID || '';
    return result;
  }
  // if the measure is a custom expression, retrieve entity and joinId using getEntityJoinIdForCustomMeasure
  return getEntityJoinIdForCustomMeasure(fieldsData, newMeasure, fallbackEntity);
};
